import { useState, useEffect } from "react";
import { useForm, Controller, Form } from "react-hook-form";
import FormInput from "./FormInput";
import { useConfig } from "../ConfigContext";
import { minDelay, readFileAsArrayBuffer } from "../util";
import Actor from "./Actor";
import BookingCalendar from "./BookingCalendar";
import LoadingOverlay from './LoadingOverlay';
import DOMPurify from "dompurify";

export default function ScreeningRequest({ caseData, onScreeningRequestComplete, onScreeningRequestFailure }) {
  const { config, error: configError } = useConfig();
  const [processingError, setProcessingError] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const {
    register,
    handleSubmit,
    watch,
    control,
    setValue,
    setError,
    formState: { errors },
    clearErrors,
    reset,
    getValues,
    resetField
  } = useForm({
    defaultValues: {
      "firstName": caseData.apt_Recipient.firstname,
      "lastName": caseData.apt_Recipient.lastname,
      "email": caseData.apt_Recipient.emailaddress1,
      "emailConfirm": caseData.apt_Recipient.emailaddress1,
      "phone": caseData.apt_Recipient.telephone2,
      "streetAddress": caseData.apt_Recipient.address1_line1,
      "municipality": caseData.apt_Recipient.address1_city,
      "province": caseData.apt_Recipient.address1_stateorprovince,
      "postalCode": caseData.apt_Recipient.address1_postalcode
    },
  });
  const [actor, requestMethod, timeslot] = watch(['actor','requestMethod','timeslot']);
  const reviewTypeInPerson =  750290002; 
  const reviewTypeVirtual = 750290001; 
  const locationTypeInPerson = 750250000; 
  const locationTypeVirtual = 750250001; 

  const onSubmit = async (data) => {
    setIsSubmitting(true);
    data.type = config.globalOptions.find(globalOption => globalOption.Name === 'apt_reviewtype')?.Options.find(option => option.Label === 'Screening')?.Value;
    data.channel = config.globalOptions.find(globalOption => globalOption.Name === 'apt_paymentchannel')?.Options.find(option => option.Label === 'Online')?.Value;

    if(data.files.length > 0){
      setIsUploading(true);
      for (const file of data.files) {
        setIsUploading(file.name);
        try {
          const binaryData = await readFileAsArrayBuffer(file);
          let response = await minDelay(fetch(`${config.apiUrl}/cases/${caseData.apt_caseid}/file/${file.name}`,{
            method: 'PUT',
            headers: {
              //'Content-Type': 'application/octet-stream',
              'Api-Key': config.apiKey,
              // 'Ocp-Apim-Trace': true
            },
            body: binaryData
          }), 1000);
          if(response.ok){
            let responseJSON = await response.json();
            console.log(responseJSON);
            file["apt_documentid"] = responseJSON.apt_documentid;
          }else{
            const responseText = await response.text();
            console.error(`Error uploading file ${file.name}: ${responseText}`);
            setError('files', { message: `Error uploading file ${file.name}: ${responseText}` });
            setIsUploading(false);
            setIsSubmitting(false);
            document.getElementById('files').scrollIntoView({ behavior: "smooth" });
            return;
          }
        } catch (error) {
          console.error(`Error uploading file ${file.name}: ${error}`);
          setError('files', { message: `Error uploading file ${file.name}` });
          setIsUploading(false);
          setIsSubmitting(false);
          return;
        }
      }
      setIsUploading(false);
    }

    try {
      let response = await minDelay(
        fetch(`${config.apiUrl}/cases/${caseData.apt_caseid}/request-review`, {
          method: "POST",
          headers: {
            "Api-Key": config.apiKey,
            "Content-Type": "application/json",
            //"Ocp-Apim-Trace": "true"
          },
          body: JSON.stringify(data),
        }),
        1000
      );

      if (response.ok) {
        setIsSubmitting(false);
        onScreeningRequestComplete();
      }
      else {
        console.error(response.statusText);
        // check for specific error of timeslot unavailable
        if (response.headers.get("Content-Type").includes("application/json")) {
          let errorJson = await response.json();
          if(errorJson.error?.message === "The timeslot selected is no longer available."){
            resetField('timeslot');
            setError('timeslot', { message: "The timeslot selected is no longer available. Please select another timeslot." });
            setIsSubmitting(false);
            return;
          }
        }
        setIsSubmitting(false);
        setProcessingError("There was an error submitting your request. Please contact us at 1-888-448-0000 or apt@york.ca.");
      }
    } catch (error) {
      console.error(error);
      setIsSubmitting(false);
      setProcessingError("There was an error submitting your request. Please contact us at 1-888-448-0000 or apt@york.ca.");
    }
  };

  const renderCalendar = (requestMethod) => {
    let startDate = new Date(caseData["apt_deemedservicedate"]);
    startDate.setHours(0,0,0,0);
    let locationType;
    switch (requestMethod) {
      case reviewTypeVirtual:
        locationType = locationTypeVirtual;
        break;
      case reviewTypeInPerson:
        locationType = locationTypeInPerson;
        break;
    
      default:
        break;
    }
    return (
      <div
        className="webform-flexbox js-webform-flexbox js-form-wrapper form-wrapper"
        style={{ marginTop: "1rem" }}
      >
        <div className="webform-flex webform-flex--1">
        <div className="webform-flex--container">
        <label className="form-item__label form-required">
                Select an available timeslot
              </label>
        <Controller
          control={control}
          name="timeslot"
          rules={{ required: "Please select an available time slot" }}
          render={({ field: { onChange, onBlur, value, ref }, fieldState: {error} }) => (
            <BookingCalendar
              onChange={onChange}
              locationType={locationType}
              service={750250000} // screening review
              startDate={startDate}
              error={error}
              value={value}
            />
          )}
        />
        </div>
        </div>
      </div>
    )
  }

  return (
    <div className="path-webform">
      <form
        className="webform-submission-form webform-submission-add-form webform-submission-test-form webform-submission-test-add-form js-webform-details-toggle webform-details-toggle antibot contextual-region"
        onSubmit={handleSubmit(onSubmit)}
        noValidate
      >
        <div
          className="webform-flexbox js-webform-flexbox js-form-wrapper form-wrapper"
          style={{ marginTop: "1rem" }}
        >
          <div className="webform-flex webform-flex--1">
            <div className="webform-flex--container">
              <FormInput
                id="actor"
                label="I am"
                type="radio"
                register={register("actor", { required: "This is required" })}
                error={errors.actor}
                options={[
                  {
                    value: "owner",
                    label:
                      "The Vehicle Plate Owner identified on the penalty order",
                  },
                  {
                    value: "authorized",
                    label:
                      "Authorized to act on behalf of the Vehicle Plate Owner",
                  },
                ]}
                required
              />
            </div>
          </div>
        </div>

        <Actor
          type="owner"
          readonly={true}
          register={register}
          control={control}
          errors={errors}
          setValue={setValue}
        />

        {actor === "authorized" && (
          <Actor
            type="authorized"
            register={register}
            control={control}
            errors={errors}
            setValue={setValue}
          />
        )}

        <div
          className="webform-flexbox js-webform-flexbox js-form-wrapper form-wrapper"
          style={{ marginTop: "1rem" }}
        >
          <div className="webform-flex webform-flex--1">
            <div className="webform-flex--container">
              <FormInput
                id="requestReason"
                label="Reason for Request"
                description="Clarify your reason for challenging: Clearly state why you believe the penalty order was issued in error and how the evidence supports your claim."
                descriptionLocation="before"
                type="textarea"
                maxLength={2000}
                register={register("requestReason", {
                  required: "A reason for the request is required",
                })}
                error={errors.requestReason}
                required
              />
            </div>
          </div>
        </div>
        <div
          className="webform-flexbox js-webform-flexbox js-form-wrapper form-wrapper"
          style={{ marginTop: "1rem" }}
        >
          <div className="webform-flex webform-flex--1">
            <div className="webform-flex--container">
              <FormInput
                id="files"
                type="file"
                label="Supporting Documents"
                description="Maximum of 3 documents (10MB maximum size)"
                control={control}
                rules={{
                  validate: (value, formValues) =>
                    (value.length === 0 || !caseData.apt_Document_apt_Case_apt_Case.find((existingDoc) => existingDoc.apt_name == value[0].name)) || "Filename already exists",
                }}
                error={errors.files}
              />
            </div>
          </div>
        </div>
        <div
          className="webform-flexbox js-webform-flexbox js-form-wrapper form-wrapper"
          style={{ marginTop: "1rem" }}
        >
          <div className="webform-flex webform-flex--1">
            <div className="webform-flex--container">
            <FormInput
                id="requestMethod"
                label="Screening Method"
                type="select"
                register={register("requestMethod", {required: "The method is required", valueAsNumber: true})}
                error={errors.requestMethod}
                options={config.globalOptions.find(globalOption => globalOption.Name === 'apt_screeningmethod')?.Options.filter(option => option.Value !== reviewTypeInPerson).map(option => ({value: option.Value,label:option.Label}))}
                required
              />
            </div>
          </div>
        </div>
        {/* CALENDAR */}
        {(requestMethod === reviewTypeVirtual || requestMethod === reviewTypeInPerson) &&
          <div>
            {renderCalendar(requestMethod)}
            {timeslot && 
            <div className="webform-flexbox js-webform-flexbox js-form-wrapper form-wrapper" style={{marginTop:'1rem'}}>
              <div className="webform-flex webform-flex--1">
                <div className="webform-flex--container">
                  <FormInput
                          id="noShowAcknowledgement"
                          label="I acknowledge that not showing up for this appointment will result in an additional fine of $60.00"
                          type="checkbox"
                          register={register("noShowAcknowledgement", {required: "You must acknowledge"})}
                          error={errors.noShowAcknowledgement}
                          required
                        />
                </div>
              </div>
            </div>
            }
          </div>
        }

        { drupalSettings?.apt_config?.terms_of_service_url &&
          <div
            className="webform-flexbox js-webform-flexbox js-form-wrapper form-wrapper"
            style={{ marginTop: "1rem" }}
          >
            <div className="webform-flex webform-flex--1">
              <div className="webform-flex--container">
                <FormInput
                  id="termsOfService"
                  label="I have read and agree to the Terms of Service"
                  type="checkbox"
                  register={register("termsOfService", {
                    required: "You must agree to the terms of service",
                  })}
                  error={errors.termsOfService}
                  required
                />
                <div className="description">
                  <div className="webform-element-description">Read terms of service <a href="/" target="_blank">here</a></div>
                </div>
              </div>
            </div>
          </div>
        }

        <div
          className="webform-flexbox js-webform-flexbox js-form-wrapper form-wrapper"
          style={{ marginTop: "1rem" }}
        >
          <div className="webform-flex webform-flex--1">
            <div className="webform-flex--container">
              <div dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(drupalSettings.apt_config.complete_request_description) }} />
              <FormInput
                id="requestComplete"
                label="I confirm that this request is complete with all supporting evidence"
                type="checkbox"
                register={register("requestComplete", {
                  required: "You must confirm that the request is complete",
                })}
                error={errors.requestComplete}
                required
              />
              
            </div>
          </div>
        </div>

        <div className="webform-flex--1 js-form-item form-item js-form-type-textfield form-type-textfield">
          <button
            id="submitButton"
            className="webform-button--submit button button--secondary js-form-submit form-submit"
            type="submit"
            disabled={isSubmitting}
          >
            {isSubmitting ? "Submitting..." : "Submit"}
          </button>
        </div>
        { processingError && <h3 className="request-processing-error">{processingError}</h3> }
      </form>
      {isSubmitting && <LoadingOverlay label={isUploading ? `Uploading ${isUploading}` : 'Submitting Request'} />}
    </div>
  );
}
